<template>
  <main-template>
    <v-row>
      <v-col>
        <span class="text-h4">
          <v-btn color="primary" text x-small @click="handleBackNavigation">
            <v-icon left> fa fa-angle-left </v-icon>
          </v-btn>
          Historico Acadêmico
          {{ courseTypeLabel }}
          -
          <span>
            {{ alunoName || "Carregando aluno..." }}
          </span>
        </span>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="pt-0 pb-0">
        <div class="d-flex mb-2 justify-space-between">
          <div>
            <v-btn class="mr-2" color="success" @click="handleAddHistorico">
              Adicionar novo ano
            </v-btn>

            <v-btn class="mr-2" color="success" @click="handleDownloadHistorico">
              Baixar Historico
            </v-btn>
          </div>

          <v-text-field
            v-model="search"
            class="ml-2 search-input"
            append-icon="mdi-magnify"
            label="Pesquisar..."
            dense
            solo
            single-line
            autofocus
            hide-details
          />
        </div>
      </v-col>

      <v-col class="pt-0">
        <v-data-table
          :headers="tableHeaders"
          :items="historicoList"
          :loading="isLoadingHistorico"
          :items-per-page="20"
          :search="search"
          :custom-filter="dataTableFilter"
          class="elevation-1"
          hide-default-footer
        >
          <template v-slot:body="{ items }">
            <tbody is="transition-group" name="scroll-x-transition" duration="150">
              <tr v-for="item of items" :key="item.id" class="text-uppercase" color="primary">
                <td class="text-lowercase">
                  {{ item.id }}
                </td>

                <td>
                  {{ item.serie || "- - -" }}
                </td>

                <td>
                  {{ item.ano || "- - -" }}
                </td>

                <td>
                  {{ item.escola || "- - -" }}
                </td>

                <td>
                  {{ item.municipio || "- - -" }}
                </td>

                <td>
                  <div v-if="item.flag" class="d-flex">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          x-small
                          class="ml-1"
                          color="blue-grey lighten-3"
                          v-bind="attrs"
                          v-on="on"
                          @click="() => handleEditHistorico(item.id)"
                        >
                          <v-icon small> fa fa-edit </v-icon>
                        </v-btn>
                      </template>
                      <span>Editar Historico</span>
                    </v-tooltip>

                    <v-tooltip bottom color="error">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          x-small
                          class="ml-1"
                          color="error"
                          v-bind="attrs"
                          v-on="on"
                          @click="() => handleDeleteHistorico(item.id)"
                        >
                          <v-icon small> fa fa-trash </v-icon>
                        </v-btn>
                      </template>
                      <span>Apagar Historico</span>
                    </v-tooltip>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </main-template>
</template>

<script lang="js">
import { dataTableFilter } from "@/plugins/searchQuery";

export default {
  name: "HistoricoList",

  data() {
    return {
      aluno: null,
      carregandoHistorico: false,
      dataTableFilter,
      historicoList: [],
      search: "",
      tableHeaders: [
        { text: "#", value: "id" },
        { text: "Serie", value: "serie" },
        { text: "Ano de Conclusão", value: "ano" },
        { text: "Escola", value: "escola" },
        { text: "Cidade", value: "municipio" },
        { text: "Ações", value: "actions", sortable: false },
      ],
    };
  },

  computed: {
    /**
     * Nome do aluno formatado
     * --
     * @returns {string} Nome completo do aluno
     */
    alunoName() {
      return this.aluno?.nomecompleto?.trim() || "";
    },

    /**
     * Tipo do curso formatado para exibição
     * --
     * @returns {string} "Ensino Fundamental" ou "Ensino Médio"
     */
    courseTypeLabel() {
      const curso = this.$router.currentRoute.params.curso;

      return curso === "fundamental" ? "Ensino Fundamental" : "Ensino Médio";
    },

    /**
     * Indica se o histórico está sendo carregado
     * --
     * @returns {boolean} Estado de carregamento
     */
    isLoadingHistorico() {
      return this.carregandoHistorico;
    },
  },

  watch: {
    /**
     * Monitora o estado de carregamento para mostrar/ocultar loader
     * --
     * @param {boolean} value - Novo estado de carregamento
     */
    carregandoHistorico(value) {
      if (value && this.historicoList.length === 0) {
        this.$loaderService.open("Carregando histórico");
      } else {
        this.$loaderService.close();
      }
    },
  },

  mounted() {
    this.initializePage();
  },

  methods: {
    /**
     * Navega para a página de criação de histórico
     */
    handleAddHistorico() {
      this.$router.push({
        name: "historico.create",
        params: {
          aluno_id: this.$router.currentRoute.params.aluno_id,
          cursoTipo: this.courseTypeLabel,
        },
      });
    },

    /**
     * Navega de volta para a lista de alunos
     */
    handleBackNavigation() {
      this.$router.push({ name: "alunos" });
    },

    /**
     * Confirma e deleta um histórico
     * --
     * @param {number|string} historico_id - ID do histórico a ser deletado
     */
    async handleDeleteHistorico(historico_id) {
      this.$modals.danger({
        title: "Cuidado!",
        message: "Tem certeza que deseja apagar este item?",
        confirmationCode: historico_id,
        confirmationMessage: `Por favor, digite <strong>${historico_id}</strong> para confirmar`,
        buttons: [
          {
            text: "Cancelar",
            callback: () => {
              this.$modals.close();
            },
            props: {
              color: "warning",
              small: true,
            },
          },
          {
            text: "Confirmar",
            disableIfCodeDoesntMatch: true,
            callback: async () => {
              await this.performDeleteHistorico(historico_id);
            },
            props: {
              color: "error",
              small: true,
            },
          },
        ],
      });
    },

    /**
     * Baixa o histórico do aluno
     */
    async handleDownloadHistorico() {
      try {
        this.$loaderService.open("Gerando Histórico...");

        const { aluno_id, curso } = this.$router.currentRoute.params;
        await this.$services.historicoService.baixarHistorico(aluno_id, curso);
      } catch (error) {
        this.handleDownloadError(error);
      } finally {
        this.$loaderService.close();
      }
    },

    /**
     * Trata erro no download do histórico
     * --
     * @param {Error} error - Objeto de erro
     */
    handleDownloadError(error) {
      if (this.isValidErrorObject(error)) {
        this.$toast.danger("Não possui histórico para baixar");
      }
    },

    /**
     * Navega para a página de edição de histórico
     * --
     * @param {number|string} historico_id - ID do histórico a ser editado
     */
    handleEditHistorico(historico_id) {
      this.$router.push({
        name: "historico.edit",
        params: { historico_id }
      });
    },

    /**
     * Inicializa a página carregando os dados necessários
     */
    async initializePage() {
      const { aluno_id, curso } = this.$router.currentRoute.params;
      await this.loadHistorico(aluno_id, curso);
    },

    /**
     * Verifica se o objeto de erro é válido
     * --
     * @param {any} error - Objeto a ser verificado
     * @returns {boolean} True se for um objeto de erro válido
     */
    isValidErrorObject(error) {
      try {
        return JSON.parse(JSON.stringify(error));
      } catch {
        return false;
      }
    },

    /**
     * Carrega o histórico do aluno
     * --
     * @param {number|string} aluno_id - ID do aluno
     * @param {string} curso - Tipo do curso
     */
    async loadHistorico(aluno_id, curso) {
      this.carregandoHistorico = true;

      try {
        const response = await this.$services.historicoService.syncAll(aluno_id, curso);
        const historicoSistema = response.historicoDoSistema || {};

        this.aluno = response.aluno || null;
        this.historicoList = [];

        await this.processExternalHistorico(response.historicoExterno);
        await this.processSystemHistorico(historicoSistema || []);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoHistorico = false;
      }
    },

    /**
     * Executa a deleção do histórico
     * --
     * @param {number|string} historico_id - ID do histórico a ser deletado
     */
    async performDeleteHistorico(historico_id) {
      this.$modals.close();
      this.$loaderService.open("Deletando item do historico");

      try {
        await this.$services.historicoService.deletarItem(historico_id);
        this.$toast.success("Historico deletado com sucesso");
        this.refreshPage();
      } catch (error) {
        this.handleDeleteError(error);
      } finally {
        this.$loaderService.close();
      }
    },

    /**
     * Trata erro na deleção do histórico
     * --
     * @param {Error} error - Objeto de erro
     */
    handleDeleteError(error) {
      this.$toast.success("Historico deletado com sucesso");
      this.$handleError(error);
    },

    /**
     * Processa o histórico externo
     * --
     * @param {Array} historicoExterno - Lista de histórico externo
     */
    async processExternalHistorico(historicoExterno) {
      if (!historicoExterno?.length) return;

      const externalItems = historicoExterno.map((item) => ({
        ano: item.ano || "- - -",
        escola: item.escola,
        flag: true,
        id: item.id,
        municipio: item.municipio,
        serie: item.serie?.descricao || "- - -",
      }));

      this.historicoList.push(...externalItems);
    },

    /**
     * Processa o histórico do sistema
     * --
     * @param {Array} historicoDoSistema - Lista de histórico do sistema
     */
    async processSystemHistorico(historicoDoSistema) {
      if (!historicoDoSistema?.length) return;

      const systemItems = historicoDoSistema.map((item) => ({
        ano: item.ano?.descricao || "- - -",
        escola: item.escola || item.turma?.franquia?.descricao,
        id: item.id,
        serie: item.serie?.descricao || item.turma?.descricao || "- - -",
        flag: false,
        municipio: item.municipio || item.turma?.franquia?.cidade,
      }));

      this.historicoList.push(...systemItems);
    },

    /**
     * Recarrega a página atual
     */
    refreshPage() {
      this.$router.go(0);
    },
  },
};
</script>

<style scoped>
.search-input {
  max-width: 300px;
}

@media (max-width: 768px) {
  .search-input {
    max-width: 200px;
  }
}

@media (max-width: 600px) {
  .search-input {
    max-width: 150px;
  }
}

.v-btn[color="success"]:hover {
  opacity: 0.9;
  transition: opacity 0.3s ease;
}

.scroll-x-transition-move {
  transition: transform 0.15s ease;
}
</style>
